/**
 * useAutoSave Hook - v1.5 Feature #1
 * Auto-saves wizard progress ONLY when data changes
 * Restores draft on page load
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';

const AUTO_SAVE_INTERVAL = 60000; // 60 seconds (reduced token refresh frequency)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface AutoSaveOptions {
  step: number;
  data: any;
  enabled: boolean;
  claimId?: string | null; // SSoT: save to Claims API when present
  reportId?: string | null; // If provided, update existing report instead of creating draft
}

interface DraftData {
  draft_id: string;
  step: number;
  data: any;
  last_modified: string;
}

export function useAutoSave({ step, data, enabled, claimId, reportId }: AutoSaveOptions) {
  const { getToken } = useAuth();
  // Initialize draftId from claimId (SSoT canonical source)
  const [draftId, setDraftId] = useState<string | null>(() => {
    return claimId || null;
  });
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // ✅ DIRTY TRACKING: Use ref (not state) to avoid triggering Wizard re-renders
  // on every data change. The only consumer is saveDraft which reads the ref.
  const isDirtyRef = useRef(false);
  const lastSavedDataRef = useRef<string | null>(null);
  
  // ✅ CRITICAL FIX: Use ref to always get latest data, avoiding stale closure issue
  const dataRef = useRef(data);
  const stepRef = useRef(step);
  const draftIdRef = useRef(draftId);
  
  // Keep refs in sync with props
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  
  useEffect(() => {
    draftIdRef.current = draftId;
  }, [draftId]);

  // SSoT sync: claimId is the canonical case identity, but it resolves ASYNC
  // (useClaimId reads it from the URL after mount), so the lazy initializer above
  // captures `null` and draftId would otherwise stay null forever. That made the
  // save log "Auto-saving draft: new" while it was actually PUT-ing /claims/{claimId},
  // and — if a save ever fired while claimId was momentarily absent — it would fork a
  // duplicate draft via POST /drafts/save. Lock draftId to claimId whenever present.
  useEffect(() => {
    if (claimId && claimId !== draftId) {
      setDraftId(claimId);
      draftIdRef.current = claimId; // update the ref eagerly so an in-flight save targets the case
    }
  }, [claimId, draftId]);

  // ✅ DIRTY TRACKING: Detect when data actually changes
  useEffect(() => {
    if (!data) return;
    
    // Create a hash of ALL saveable fields — including financial data which
    // is computed by the frontend from bank statement extractions.
    // Without tracking these, computed data never gets auto-saved.
    const userEditableData = {
      client_name: data.client_name,
      claimant_name: data.claimant_name,
      business_address: data.business_address,
      zone: data.zone,
      business_type: data.business_type,
      event_type: data.event_type,
      event_date: data.event_date,
      event_start_date: data.event_start_date,
      event_end_date: data.event_end_date,
      claimant_email: data.claimant_email,
      law_firm: data.law_firm,
      // Include computed data — frontend computes these from uploads
      financial_data_keys: data.financial_data ? Object.keys(data.financial_data).sort().join(',') : '',
      tax1065Files_count: data.tax1065Files?.length || 0,
      cached_anomaly_hash: data.cached_anomaly_analysis?.dataHash || '',
    };
    
    const currentHash = JSON.stringify(userEditableData);
    
    if (lastSavedDataRef.current === null) {
      // First load - set baseline, don't mark dirty
      lastSavedDataRef.current = currentHash;
      console.log('📋 Auto-save: Set initial baseline (not dirty)');
    } else if (currentHash !== lastSavedDataRef.current) {
      // User changed something
      isDirtyRef.current = true;
      console.log('✏️ Auto-save: Data changed, marking dirty');
    }
  }, [data]);

  // Auto-save function - uses refs to get latest data
  // Can also accept explicit data to avoid timing issues with state updates
  const saveDraft = useCallback(async (explicitData?: any, forcesSave: boolean = false) => {
    if (!enabled) return;
    
    // ✅ DIRTY TRACKING: Only save if user changed something (unless forced)
    if (!isDirtyRef.current && !forcesSave && !explicitData) {
      console.log('⏭️ Skipping auto-save: No changes detected');
      return;
    }
    
    // Use explicit data if provided, otherwise use refs for latest values
    const currentData = explicitData || dataRef.current;
    const currentStep = stepRef.current;
    const currentDraftId = draftIdRef.current;
    
    console.log('💾 saveDraft called:', {
      hasExplicitData: !!explicitData,
      isDirty: isDirtyRef.current,
      forcesSave,
      tax1065Files: currentData?.tax1065Files?.length || 0,
      draftId: currentDraftId
    });

    // Don't save if we're on Step 1 with no data entered
    const hasData = currentData && (
      currentData.client_name || 
      currentData.claimant_name || 
      currentData.business_address ||
      currentData.zone ||
      currentData.business_type ||
      (currentData.financial_data && Object.keys(currentData.financial_data).length > 0)
    );

    if (currentStep === 1 && !hasData) {
      console.log('⏭️ Skipping auto-save: Step 1 with no data');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      // FIX: Add retry logic for token refresh errors
      let token;
      try {
        token = await getToken();
      } catch (tokenError) {
        console.warn('⚠️ Token refresh failed, retrying...', tokenError);
        // Wait 1 second and retry once
        await new Promise(resolve => setTimeout(resolve, 1000));
        try {
          token = await getToken();
        } catch (retryError) {
          console.error('❌ Token refresh failed after retry:', retryError);
          throw new Error('Authentication failed. Please refresh the page.');
        }
      }

      // SSoT: Use Claims API when claimId is present
      let endpoint: string;
      let method: string;
      if (claimId) {
        endpoint = `${BACKEND_URL}/api/v1/claims/${claimId}`;
        method = 'PUT';
      } else if (reportId) {
        endpoint = `${BACKEND_URL}/api/v1/reports/${reportId}/update`;
        method = 'PUT';
      } else {
        endpoint = `${BACKEND_URL}/api/v1/reports/drafts/save`;
        method = 'POST';
      }

      console.log(`💾 Auto-saving ${reportId ? 'report' : 'draft'}:`, reportId || currentDraftId || 'new');
      console.log(`🔴 CRITICAL DEBUG - tax1065Files:`, currentData.tax1065Files?.length || 0);
      console.log(`🔴 plFiles in data:`, currentData.plFiles?.length || 0);

      // CRITICAL FIX: Use XMLHttpRequest instead of fetch() to avoid Clerk rrweb-recorder
      // "Response.clone: Body has already been consumed" error
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, endpoint);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch (e) {
              reject(new Error('Failed to parse save response'));
            }
          } else {
            reject(new Error(`Failed to save: ${xhr.status} ${xhr.responseText}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during save'));
        // 🔍 DEBUG: Log what data we're sending
        console.log('🔍 AUTO-SAVE PAYLOAD:', {
          step: currentStep,
          draft_id: currentDraftId,
          plFiles_length: currentData.plFiles?.length || 0,
          uploadedDocuments_length: currentData.uploadedDocuments?.length || 0,
          tax1065Files_length: currentData.tax1065Files?.length || 0,
          has_financial_data: !!currentData.financial_data,
          data_keys: Object.keys(currentData)
        });
        
        // Strip backend-computed fields — these are set by backend processing
        // (OCR extraction, reprocessing) and must not be overwritten by stale
        // frontend state. The backend update_claim also guards these, but the
        // initial INSERT (force-save) doesn't have that protection.
        //
        // Every time a new backend-computed field is added, update BOTH this
        // list AND backend_computed_fields in claim_service.update_claim.
        const backendComputedFields = [
          'tax1065Files', 'tax_data', 'canonical_data',
          'processedFiles', 'last_upload_job_id',
          'bankStatementFiles', 'plFiles',
          'unified_upload_data', 'financial_data', 'calculated_years',
          // Revenue Sources Phase 2 — merchant settlement statements + their
          // reconciliation summary are populated by the upload pipeline and
          // the on-demand reconcile endpoint.  The UI reads them but never
          // authors them.
          'revenue_source_statements', 'revenue_source_reconciliation',
        ];
        const safeData = { ...currentData };
        for (const field of backendComputedFields) {
          delete safeData[field];
        }

        xhr.send(JSON.stringify({
          draft_id: currentDraftId,
          step: currentStep,
          data: safeData,
          last_modified: new Date().toISOString()
        }));
      });
      
      // Store draft_id for future saves (legacy path only — Claims API uses claimId from URL)
      if (result.draft_id && !currentDraftId && !claimId) {
        setDraftId(result.draft_id);
        draftIdRef.current = result.draft_id;
      }

      setLastSaved(new Date());
      
      // ✅ Reset dirty flag and update baseline after successful save
      isDirtyRef.current = false;
      const userEditableData = {
        client_name: currentData.client_name,
        claimant_name: currentData.claimant_name,
        business_address: currentData.business_address,
        zone: currentData.zone,
        business_type: currentData.business_type,
        event_date: currentData.event_date,
        event_end_date: currentData.event_end_date,
        claimant_email: currentData.claimant_email,
        law_firm: currentData.law_firm,
      };
      lastSavedDataRef.current = JSON.stringify(userEditableData);
      
      console.log('✅ Auto-save successful:', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      setSaveError('Failed to auto-save');
    } finally {
      setIsSaving(false);
    }
  }, [enabled, getToken, reportId, claimId]); // isDirty removed — reads from ref now

  // Restore draft on mount
  const restoreDraft = async (): Promise<DraftData | null> => {
    try {
      const draft = await api.get('/api/v1/reports/drafts/latest', { showErrorToast: false });
      
      if (draft && draft.draft_id) {
        setDraftId(draft.draft_id);
        return draft;
      }

      return null;
    } catch (error) {
      console.error('Error restoring draft:', error);
      return null;
    }
  };

  // Delete draft (call after successful submission)
  const deleteDraft = async () => {
    if (!draftId) return;

    try {
      await api.delete(`/api/v1/reports/drafts/${draftId}`, { showErrorToast: false });
      setDraftId(null);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  // Set up auto-save interval
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    // Save immediately on data change (debounced by interval)
    intervalRef.current = setInterval(() => {
      saveDraft();
    }, AUTO_SAVE_INTERVAL);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, saveDraft]); // step/data/draftId read from refs inside saveDraft

  // Manual save function (forces save even if not dirty) — stable reference.
  // Returns the underlying Promise<void> from saveDraft so callers (e.g.
  // Step3.onSave) can await it and chain post-save work.
  const saveNow = useCallback((explicitData?: any) => {
    return saveDraft(explicitData, true);
  }, [saveDraft]);

  return {
    draftId,
    lastSaved,
    isSaving,
    saveError,
    saveNow,
    restoreDraft,
    deleteDraft
  };
}
